import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPillarData, fetchPresurveyData, savePresurveyData } from '../../services/PreSurveyService';

const initialState = {
  presurveyData: null,
  pillarData: [],
  supplierName: '',
  reportCreationYear: new Date().getFullYear(),
  periodStartDate: null,
  periodEndDate: null,
  cdpDataAvailable: false,
  savedCdp: false,
  otherDataToReport: false,
  savedOtherDataToReport: false,
  focusAreas: {},
  savedFocusAreas: {}, // New state to track saved focus areas
  loading: false,
  error: null,
  activeTabKey: 'preSurvey',
  activeNestedTabKey: '',
  isPresurveySubmitted: false, // New state to track if presurvey is submitted
  tabList: [
    {
      key: "preSurvey",
      tab: <span className="custom-tab-label">Pre-Survey</span>,
    },
  ],
};

const updateTabs = (state) => {
  let updatedTabs = [
    { key: "preSurvey", tab: <span className="custom-tab-label">Pre-Survey</span> },
  ];

  if (state.savedCdp) {
    updatedTabs = [...updatedTabs, { key: "cdpDetails", tab: <span className="custom-tab-label">CDP Details</span> }];
  }

  if (state.savedOtherDataToReport) {
    updatedTabs = [...updatedTabs, { key: "reporting", tab: <span className="custom-tab-label">Reporting</span> }];
  }

  return updatedTabs;
};

const formatDateToFrontend = (dateString) => {
  if (!dateString || isNaN(new Date(dateString).getTime())) return null;
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const fetchPillarDataThunk = createAsyncThunk(
  'presurvey/fetchPillarData',
  async (_, thunkAPI) => {
    try {
      const data = await fetchPillarData();
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchPresurveyDataThunk = createAsyncThunk(
  'presurvey/fetchPresurveyData',
  async (supplierId, thunkAPI) => {
    try {
      const data = await fetchPresurveyData(sessionStorage.getItem("supplierId"));     
      return {
        ...data,
        focusAreas: data.focusAreas || {},
        savedFocusAreas: data.focusAreas || {}, // Initialize saved focus areas
        reportCreationYear: new Date().getFullYear(),
        periodStartDate: formatDateToFrontend(data.periodStartDate),
        periodEndDate: formatDateToFrontend(data.periodEndDate),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);
// Add this with your other thunk exports
export const goToNextTabThunk = () => (dispatch, getState) => {
  const state = getState().presurvey;
  
  // Update the tabList first to ensure it's current
  dispatch(setCdpSaved(state.cdpDataAvailable));
  dispatch(setpreSave(state.otherDataToReport));
  
  // Get the updated state after dispatching the above actions
  const updatedState = getState().presurvey;
  
  // Find the index of the current active tab
  const currentIndex = updatedState.tabList.findIndex(
    tab => tab.key === updatedState.activeTabKey
  );
  
  // Navigate to next tab if available
  if (currentIndex < updatedState.tabList.length - 1) {
    const nextTabKey = updatedState.tabList[currentIndex + 1].key;
    dispatch(setTabKey(nextTabKey));
  } else {
    // Handle the case when there's no next tab
    // You'll need to import notification from wherever it's defined
    notification.info({ message: "You've reached the end of the survey." });
  }
};

export const savePresurveyDataThunk = createAsyncThunk(
  'presurvey/savePresurveyData',
  async ({ supplierId, data }, thunkAPI) => {
    try {
      const response = await savePresurveyData(supplierId, data);
      return {
        ...response,
        reportCreationYear: new Date().getFullYear(),
        periodStartDate: formatDateToFrontend(response.periodStartDate),
        periodEndDate: formatDateToFrontend(response.periodEndDate),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const presurveySlice = createSlice({
  name: 'presurvey',
  initialState,
  reducers: {
    resetForm: (state) => {
      // Keep the commented out code for reference
      // state.supplierName = '';
      // state.reportCreationYear = new Date().getFullYear();
      // state.periodStartDate = null;
      // state.periodEndDate = null;
      // state.cdpDataAvailable = false;
      // state.otherDataToReport = false;
      // state.focusAreas = {};
      // state.savedFocusAreas = {};
      // state.savedCdp=false;
      // state.savedOtherDataToReport=false;
      // state.isPresurveySubmitted = false;
    },
    setFormField: (state, action) => {
      const { field, value } = action.payload;
      if (field === 'focusAreas') {
        state.focusAreas = typeof value === 'object' ? value : {};
      } else {
        state[field] = value;
      }
    },
    setCdpSaved: (state, action) => {
      state.savedCdp = action.payload;
      state.tabList = updateTabs(state);
    },
    setpreSave: (state, action) => {
      state.savedOtherDataToReport = action.payload;
      state.tabList = updateTabs(state);
    },
    setSavedFocus: (state, action) => {
      state.savedFocusAreas = { ...action.payload };
      state.tabList = updateTabs(state);
    },
    setTabKey: (state, action) => {
      state.activeTabKey = action.payload;
    },
    setPresurveySubmitted: (state, action) => {
      state.isPresurveySubmitted = action.payload;
      if (action.payload) {
        state.savedFocusAreas = { ...state.focusAreas };
        state.savedCdp = state.cdpDataAvailable;
        state.savedOtherDataToReport = state.otherDataToReport;
        state.tabList = updateTabs(state);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPillarDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPillarDataThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.pillarData = action.payload;
      })
      .addCase(fetchPillarDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPresurveyDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPresurveyDataThunk.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        state.id = data.id;
        state.supplierId = data.supplierId;
        state.supplierName = data.supplierName;
        state.reportCreationYear = data.reportCreationYear;
        state.periodStartDate = data.periodStartDate;
        state.periodEndDate = data.periodEndDate;
        state.cdpDataAvailable = data.cdpDataAvailable;
        state.otherDataToReport = data.otherDataToReport;
        state.focusAreas = {
          Energy: data.focusAreas.Energy || false,
          Waste: data.focusAreas.Waste || false,
          Packaging: data.focusAreas.Packaging || false,
        };
        state.savedFocusAreas = { ...state.focusAreas };
        state.savedCdp = state.cdpDataAvailable;
        state.savedOtherDataToReport = state.otherDataToReport;
        state.tabList = updateTabs(state);
      })
      .addCase(fetchPresurveyDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(savePresurveyDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(savePresurveyDataThunk.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        state.periodStartDate = data.periodStartDate;
        state.periodEndDate = data.periodEndDate;
        state.savedFocusAreas = { ...state.focusAreas };
        state.savedCdp = state.cdpDataAvailable;
        state.savedOtherDataToReport = state.otherDataToReport;
        state.isPresurveySubmitted = true;
        state.tabList = updateTabs(state);
      })
      .addCase(savePresurveyDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetForm, setFormField, setTabKey, setPresurveySubmitted, setCdpSaved, setpreSave, setSavedFocus } = presurveySlice.actions;

export default presurveySlice.reducer;